<template>
  <div id="Manufacturing">
    <SectionManufacturingHero />
    <SectionTrust />
    <SectionReducedEnergyCosts />
    <SectionLackTransitionPlans />
    <SectionManufacturingPlatform />
    <SectionGHGDemo />
    <SectionTrackRecord />
    <SectionManufacturingPricing />
  </div>
</template>

<script setup lang="ts">
import SectionTrust from '@/views/components/sections/SectionTrust.vue'
import SectionManufacturingHero from './sections/SectionManufacturingHero.vue'
import SectionManufacturingPlatform from './sections/SectionManufacturingPlatform.vue'
import SectionReducedEnergyCosts from './sections/SectionReducedEnergyCosts.vue'
import SectionManufacturingPricing from './sections/SectionManufacturingPricing.vue'
import SectionTrackRecord from '@/views/components/sections/SectionTrackRecord.vue'
import SectionLackTransitionPlans from './sections/SectionLackTransitionPlans.vue'
import { useSeo, getBreadcrumbStructuredData, getFAQStructuredData } from '@/composables/useSeo'
import { getSeoConfig } from '@/config/seo'
import SectionGHGDemo from '@/views/components/sections/SectionGHGDemo.vue'

// FAQ Data for structured data
const faqData = [
  {
    question: "How does manufacturing sustainability software help reduce emissions?",
    answer: "Manufacturing sustainability software tracks Scope 1 process emissions, energy consumption, and supply chain impacts in real-time. It identifies emission hotspots, models reduction initiatives, and provides data-driven insights to optimize production processes for lower carbon intensity."
  },
  {
    question: "Can Sustainalyse integrate with our ERP or MES systems?",
    answer: "Yes, Sustainalyse integrates with major ERP systems (SAP, Oracle, Microsoft Dynamics) and MES platforms to automatically pull production data, energy consumption, and material usage. This eliminates manual data entry and ensures real-time emissions tracking aligned with manufacturing operations."
  },
  {
    question: "What ESRS standards apply to manufacturing companies?",
    answer: "Manufacturing companies must comply with ESRS E1 (Climate Change including Scope 1, 2, 3 emissions), E2 (Pollution from industrial processes), E3 (Water resources), E4 (Biodiversity), and E5 (Circular Economy including waste management). Sustainalyse provides automated tracking and reporting for all applicable ESRS standards."
  },
  {
    question: "How do we measure Scope 3 emissions from our supply chain?",
    answer: "Sustainalyse helps you measure Scope 3 supply chain emissions through supplier engagement portals, spend-based calculations, and product-level carbon footprinting. We support EPD (Environmental Product Declaration) data integration and supplier-specific emission factors for accurate supply chain accounting."
  },
  {
    question: "Can we track emissions by product or production line?",
    answer: "Yes, Sustainalyse enables product-level and production line-level carbon accounting. Track emissions intensity per unit produced, compare carbon footprints across product lines, and identify opportunities for process optimization and low-carbon product development."
  }
]

// SEO Configuration with FAQ schema
useSeo(
  getSeoConfig('manufacturing'),
  [
    getBreadcrumbStructuredData([
      { name: 'Home', url: 'https://sustainalyse.com/' },
      {
        name: 'Manufacturing Sustainability',
        url: 'https://sustainalyse.com/manufacturing',
      },
    ]),
    getFAQStructuredData(faqData)
  ]
)
</script>

<style lang="scss" scoped>
#Manufacturing {
  min-height: 100vh;
  background-color: $color-ui-gray-cool-50;
}
</style>
